import { isEmpty, isNil } from 'lodash';
import {
  CSSProperties,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { MBTextInput } from '@shared/components/textInput';
import { NumberField } from '@shared/data/entity/fields/NumberField';
import { useComponentFocus } from '@shared/scenes/cards/form/components/ComponentFocus';
import { useResponsiveFormContext } from '@shared/scenes/entity/components/ResponsiveFormContext';
import { getComponentPlaceholder, isNumberComponent } from '@shared/scenes/workflows/utils';
import { ColorThemeContext, grid } from '@shared/styles';
import { API } from '@shared/types/api';
import { useBoolean } from '@shared/util/hooks';
import { EditFieldContainer } from './EditFieldContainer';

interface Props {
  field: NumberField | null;
  component: API.NumberComponent | API.RelatedCardLookupComponent;
  updateField?: (componentId: string, field: NumberField | null) => void;
  error?: string;
  allowClear?: boolean;
  showIcon?: boolean;
  label?: string;
  showLabel?: boolean;
  indentLabel?: boolean;
  reserveIconSpace?: boolean;
  compactTop?: boolean;
  compactBottom?: boolean;
  isGrayScale?: boolean;
  isItalic?: boolean;
  subfooter?: ReactNode;
  style?: CSSProperties;
}

export function NumberEditField({
  field,
  component,
  updateField,
  error,
  allowClear,
  showIcon,
  label,
  showLabel,
  indentLabel,
  reserveIconSpace,
  compactTop,
  compactBottom,
  subfooter,
  isGrayScale,
  isItalic,
  style,
}: Props) {
  const [input, setInput] = useState(field?.editableValue ?? '');
  const colors = useContext(ColorThemeContext);
  const [hasInput, onInput, onClearInput] = useBoolean(false);
  const [isFocused, onFocus, onBlur] = useBoolean(false);
  const { setComponentFocused, setComponentBlurred } = useComponentFocus();
  const showLocalError = !isFocused;
  const showError = (!hasInput || showLocalError) && !isEmpty(error);
  const format = isNumberComponent(component) ? component.format : field?.format ?? 'FLOAT';

  useEffect(() => {
    if (isNil(field?.value) && !isEmpty(input) && !isFocused) {
      setInput('');
    }
  }, [field?.value, input, isFocused]);

  const handleFocus = useCallback(() => {
    onFocus();
    setComponentFocused();
  }, [onFocus, setComponentFocused]);

  const handleBlur = useCallback(() => {
    onBlur();
    setComponentBlurred();
    setInput(prev => {
      // pass input through storedValue parser to remove non-numeric input on blur
      const storedValue = NumberField.toStoredValue(prev, format);
      return NumberField.toEditableValue(storedValue, format);
    });
  }, [onBlur, setComponentBlurred, format]);

  const onChangeText = useCallback(
    (textValue: string) => {
      if (!isNumberComponent(component)) {
        return;
      }
      if (textValue === '.') {
        setInput('0.');
        return;
      }

      if (isEmpty(textValue)) {
        setInput(textValue);
        onClearInput();
        updateField?.(component.id, null);
        return;
      }

      // remove all non-numeric input, allowing number-related characters
      const cleanedInput = NumberField.removeInvalidInput(textValue);
      setInput(cleanedInput);

      const numericValue = NumberField.toStoredValue(cleanedInput, component.format);
      if (isNil(numericValue)) {
        return;
      }

      onInput();
      updateField?.(component.id, new NumberField(numericValue, component.format));
    },
    [updateField, component, onClearInput, onInput],
  );

  const onClear = useCallback(() => {
    onClearInput();
    setInput('');
    updateField?.(component.id, null);
  }, [updateField, component.id, setInput, onClearInput]);

  const isReadOnly = component.readonly || isNil(updateField);
  const hasValue = !isEmpty(input);
  const showClearButton = hasValue && !isReadOnly && allowClear;
  const storableValue = NumberField.toStoredValue(input, format);
  const formattedValue = NumberField.toFormattedValue(storableValue, format);

  const responsiveForms = useResponsiveFormContext();

  return (
    <EditFieldContainer
      component={component}
      showIcon={showIcon}
      label={label}
      showLabel={!isNil(showLabel) ? showLabel : responsiveForms || hasValue}
      indentLabel={indentLabel}
      reserveIconSpace={reserveIconSpace}
      showClearButton={showClearButton}
      onClear={onClear}
      error={showError ? error : undefined}
      compactTop={compactTop}
      compactBottom={compactBottom}
      subfooter={subfooter}
      isGrayScale={isGrayScale}
      isItalic={isItalic}
      style={style}
    >
      <MBTextInput
        noBorder
        noPadding
        inputMode="decimal"
        keyboardType="decimal-pad"
        onBlur={isReadOnly ? undefined : handleBlur}
        onFocus={isReadOnly ? undefined : handleFocus}
        value={isFocused ? input : formattedValue}
        onChangeText={onChangeText}
        placeholder={getComponentPlaceholder(component)}
        placeholderColor={colors.foregroundInactive}
        editable={!isReadOnly}
        style={{ paddingVertical: !hasValue ? grid(1.25) : undefined }}
      />
    </EditFieldContainer>
  );
}
